import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { OrgModal, PrimaryButton } from "../organisation/OrgModal";
import { can, readSession } from "../../utils/organisationApi";
import useResetOnReturn from "../../hooks/useResetOnReturn";
import { PLAN_BLOCKED_EVENT, PLAN_STALE_EVENT } from "../../utils/planEvents";
import { bannerFor, blockedMessage, openBillingPortal, PLAN_PATH, PLAN_STATE, planState, refreshPlan, UPGRADE_PATH, usePlan } from "../../utils/subscriptionData";

// Re-read the plan when the tab comes back after this long. The server stays the authority.
const STALE_MS = 30000;
const MAX_TIMER_MS = 2147483647;

// Keeps the shared plan current: on load, when the tab becomes visible again, and the moment a
// trial is due to end while someone is still logged in.
const usePlanSync = (enabled) => {
  const { data, loadedAt } = usePlan();
  const endAt = data?.plan?.active ? data.plan.end_at : null;

  useEffect(() => {
    if (!enabled) return undefined;
    refreshPlan().catch(() => {});
    const onWake = () => {
      if (document.visibilityState === "visible" && Date.now() - (loadedAt || 0) > STALE_MS) refreshPlan().catch(() => {});
    };
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);
    return () => {
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
    };
    // loadedAt is read when the event fires, not a reason to re-subscribe
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !endAt) return undefined;
    const wait = new Date(endAt).getTime() - Date.now() + 1500;
    if (wait <= 0) return undefined;
    const timer = setTimeout(() => refreshPlan().catch(() => {}), Math.min(wait, MAX_TIMER_MS));
    return () => clearTimeout(timer);
  }, [enabled, endAt]);
};

// Re-read usage shortly after a save that may have used an allowance. Several saves close together make one
// request, so bulk actions don't flood the API.
const usePlanRefreshAfterSaves = (enabled) => {
  useEffect(() => {
    if (!enabled) return undefined;
    let timer = null;
    const onStale = () => {
      clearTimeout(timer);
      timer = setTimeout(() => refreshPlan().catch(() => {}), 800);
    };
    window.addEventListener(PLAN_STALE_EVENT, onStale);
    return () => {
      clearTimeout(timer);
      window.removeEventListener(PLAN_STALE_EVENT, onStale);
    };
  }, [enabled]);
};

const TONES = {
  info: "bg-[#eef3fb] text-[#1b3f7a] border-[#c9d8f0]",
  // A trial that is running with time to spare: its own colour, so it reads differently from "buy a plan"
  trial: "bg-[#f1eefe] text-[#3d2a8c] border-[#d8cffa]",
  warn: "bg-[#fff6e0] text-[#6b4700] border-[#f1d9a0]",
  danger: "bg-[#fdecea] text-[#8a1c14] border-[#f5c2bd]",
};

// The last plan seen for this organisation, so the banner is drawn on the first paint instead of
// popping in (and pushing the page down) once the request returns. Display only: gating still waits
// for the server's answer.
const cacheKey = (orgId) => `sifthire:last-plan:${orgId || "none"}`;

const readCachedPlan = (orgId) => {
  try {
    const raw = localStorage.getItem(cacheKey(orgId));
    return raw ? JSON.parse(raw).plan ?? null : undefined;
  } catch {
    return undefined;
  }
};

const writeCachedPlan = (orgId, plan) => {
  try {
    localStorage.setItem(cacheKey(orgId), JSON.stringify({ plan }));
  } catch {
    // Without storage the banner just appears after the request, as before
  }
};

const PlanBanner = ({ canBill, orgId }) => {
  const navigate = useNavigate();
  const { data, status } = usePlan();
  const [portalBusy, setPortalBusy] = useState(false);
  useResetOnReturn(() => setPortalBusy(false));
  const [cached] = useState(() => readCachedPlan(orgId));

  useEffect(() => {
    if (data) writeCachedPlan(orgId, data.plan ?? null);
  }, [data, orgId]);

  // A failed check isn't an expired plan: say it couldn't be verified, and leave actions to the server
  if (status === "error" && !data) {
    return (
      <div role="status" className={`w-full flex flex-wrap items-center justify-between gap-2 px-4 sm:px-6 max-sm:pl-12 py-2 text-sm border-b ${TONES.warn}`}>
        <span>We couldn't verify your subscription just now.</span>
        <button type="button" onClick={() => refreshPlan().catch(() => {})} className="font-semibold underline">
          Try again
        </button>
      </div>
    );
  }

  // Until the first answer arrives, draw from the last known plan; nothing if there is none
  const shownPlan = data ? data.plan : cached;
  const banner = shownPlan === undefined ? null : bannerFor(shownPlan, canBill);
  if (!banner) return null;

  const run = async () => {
    if (banner.action.portal) {
      setPortalBusy(true);
      try {
        const url = await openBillingPortal();
        if (url) window.location.assign(url);
        else navigate(PLAN_PATH);
      } catch {
        navigate(PLAN_PATH);
      } finally {
        setPortalBusy(false);
      }
      return;
    }
    navigate(banner.action.to);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className={`w-full flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 sm:px-6 max-sm:pl-12 py-3 text-sm border-b ${TONES[banner.tone]}`}
    >
      <div className="min-w-0">
        <p className="font-semibold">{banner.title}</p>
        {banner.body && <p>{banner.body}</p>}
      </div>
      {banner.action && (
        <button
          type="button"
          onClick={run}
          disabled={portalBusy}
          className="shrink-0 rounded bg-accent-blue px-3 py-1.5 font-semibold text-white hover:bg-[#1D4690] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black disabled:opacity-60"
        >
          {portalBusy ? "Opening..." : banner.action.label}
        </button>
      )}
    </div>
  );
};

const DIALOG_TITLES = {
  PLAN_LIMIT_REACHED: "Plan limit reached",
  RESUME_LIMIT_REACHED: "Resume limit reached",
};

// Opens when the backend refuses an action for plan reasons, or when a gated control is activated.
// One dialog for every page, so the explanation is the same everywhere. An ended plan says "Upgrade",
// an organisation that never had a plan says "Buy plan".
const PlanBlockedDialog = ({ canBill }) => {
  const navigate = useNavigate();
  const { data } = usePlan();
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const onBlocked = (event) => {
      setDetail(event.detail || {});
      // Meters and disabled buttons should match what the server just said
      refreshPlan().catch(() => {});
    };
    window.addEventListener(PLAN_BLOCKED_EVENT, onBlocked);
    return () => window.removeEventListener(PLAN_BLOCKED_EVENT, onBlocked);
  }, []);

  if (!detail) return null;
  const code = detail.code || "PLAN_LIMIT_REACHED";
  const noPlan = code === "NO_ACTIVE_PLAN";
  const neverHadPlan = noPlan && planState(data?.plan) === PLAN_STATE.LEGACY;
  const title = !noPlan ? DIALOG_TITLES[code] || DIALOG_TITLES.PLAN_LIMIT_REACHED : neverHadPlan ? "Buy plan" : "Upgrade";
  const actionLabel = neverHadPlan ? "Buy plan" : "Upgrade now";
  // The server's text can mention upgrading; people without billing access get the contact-your-admin version
  const message = !canBill
    ? blockedMessage({ reason: noPlan ? "no_plan" : "limit", metric: detail.metric, canBill })
    : neverHadPlan
      ? "You don't have a plan yet, so this feature isn't available. Please buy a plan to continue."
      : noPlan
        ? "Your plan has ended, so this feature isn't available. Please upgrade to continue."
        : detail.message || blockedMessage({ reason: "limit", metric: detail.metric, canBill });
  const close = () => setDetail(null);
  const go = () => {
    close();
    navigate(noPlan ? UPGRADE_PATH : PLAN_PATH);
  };

  // On document.body and above everything, so it covers a dialog that is already open (for example Schedule interview)
  return createPortal(
    <OrgModal
      isOpen
      above
      onClose={close}
      titleId="plan-blocked-title"
      title={canBill ? title : "Plan unavailable"}
      footer={
        canBill ? (
          <PrimaryButton onClick={go}>{noPlan ? actionLabel : "Upgrade or add-ons"}</PrimaryButton>
        ) : (
          <PrimaryButton onClick={close}>OK</PrimaryButton>
        )
      }
    >
      <p role="alert">{message}</p>
      {!canBill && <p className="text-sm text-accent-gray-light">Your existing information stays available to view.</p>}
    </OrgModal>,
    document.body
  );
};

// Mounted once in the main layout: keeps the plan current, shows the status banner, and hosts the
// shared "blocked by plan" dialog.
const PlanStatus = () => {
  const session = useMemo(() => readSession(), []);
  const canBill = can(session, "manageBilling");
  // A Super Admin outside any organisation has no plan to check
  usePlanSync(Boolean(session.role));
  usePlanRefreshAfterSaves(Boolean(session.role));
  return (
    <>
      {/* A Super Admin who has entered someone else's organisation isn't asked to buy a plan for it */}
      {session.role && !session.isSupportSession && <PlanBanner canBill={canBill} orgId={session.organisationId} />}
      <PlanBlockedDialog canBill={canBill} />
    </>
  );
};

export default PlanStatus;
